import { comparePlateValues, diffPositions, MAX_PLATE_VALUE_LEN, plateTruncatesRecord } from './compare';
import { KNOWN_MAKES, isKnownMake } from './knownMakes';
import { createConfirmer, type Confirmer } from './confirm';
import { classifyRecordValue, normalizePlate } from './normalize';
import { junkFlags } from './junkRules';
import { sanitizeDisplay } from './sanitize';
import type { PlateReading, Verdict, VerdictComponent, VerdictField, VerdictKind, VerdictReason } from './types';

export { MIN_OCR_CONFIRM_LEN } from './confirm';

export interface VerdictInput {
  component: VerdictComponent;
  field: VerdictField;
  /** The value being judged: the stored record value, or a value a person entered/proposed. */
  comparedValue: string | null;
  comparedSource: 'record' | 'human';
  /** The unit's stored serial for this component (identity anchor). */
  storedSerial: string | null;
  /**
   * True when the unit stores any ATS 2 value: an unlabeled ATS plate is then ambiguous.
   * REQUIRED, and anything other than an explicit `false` is treated as true (fail closed).
   */
  unitHasAts2: boolean;
  readings: readonly PlateReading[];
}

type AnchorState = 'pass' | 'mismatch' | 'unchecked';

interface PhotoFacts {
  photoId: string;
  storagePath: string;
  plateValue: string | null;
  photosRead: number;
  photosAgreeing: number;
}

function componentMatches(r: PlateReading, c: VerdictComponent, ambiguousAts: boolean): boolean {
  if (r.readerComponent === 'not-a-plate') return false;
  const readerSays = r.readerComponent === 'generator-set' ? 'generator' : r.readerComponent;
  if (r.labeledComponent) {
    if (r.labeledComponent !== c) return false;
    // A label decides alone only when the reader could not name a component; a concrete disagreement is unsure.
    if (readerSays === 'cannot-tell' || readerSays === 'other') return true;
    return readerSays === c || (readerSays === 'ats' && c === 'ats2');
  }
  // An unlabeled ATS plate cannot say which of two switches it is: it decides `ats` on a one-ATS unit only, never `ats2`.
  if (readerSays === 'ats') return c === 'ats' && !ambiguousAts;
  return readerSays === c;
}

/** capturedAt as a sortable number; missing or unparseable ranks last (-Infinity), never as the epoch. */
function capturedTime(r: PlateReading): number {
  if (!r.capturedAt) return -Infinity;
  const t = Date.parse(r.capturedAt);
  return Number.isNaN(t) ? -Infinity : t;
}

/** Barcode-confirmed first, then newest capturedAt (invalid/null last), then input order. Never mutates its input. */
function pickBest(pool: readonly PlateReading[], field: VerdictField, cf: Confirmer): PlateReading {
  const rank = (r: PlateReading) => (cf.of(r, field) === 'barcode' ? 1 : 0);
  return pool.reduce((best, r) =>
    rank(r) > rank(best) || (rank(r) === rank(best) && capturedTime(r) > capturedTime(best)) ? r : best);
}

const empty = (input: VerdictInput, kind: VerdictKind, reason: VerdictReason, photosRead = 0): Verdict => ({
  component: input.component, field: input.field, kind, confidence: 'low', reason,
  plateValue: null, comparedValue: sanitizeDisplay(input.comparedValue), photoId: null, storagePath: null,
  diffPositions: [], photosRead, photosAgreeing: 0,
});

/** Identity anchor for one photo: does ITS OWN confirmed serial tie it to this unit's stored serial? */
function anchorState(input: VerdictInput, cf: Confirmer, r: PlateReading): AnchorState {
  const serial = r.fields?.serial;
  if (serial?.status !== 'read' || cf.of(r, 'serial') === null) return 'unchecked';
  if (classifyRecordValue(input.storedSerial) === 'blank' || isJunkRecord(input.storedSerial, input.component, 'serial', 'record')) return 'pass';
  const cmp = comparePlateValues(serial.value, input.storedSerial);
  if (cmp === 'same' || cmp === 'format-variant') return 'pass';
  // A photo serial that is all of the stored serial plus more names a DIFFERENT serial (the cross-unit signal); one that is only
  // part of the stored serial may be a partial read of this unit's plate, so it cannot tie or untie the photo.
  if (cmp === 'contained') return normalizePlate(serial.value).length > normalizePlate(input.storedSerial).length ? 'mismatch' : 'unchecked';
  return cmp === 'different' ? 'mismatch' : 'unchecked';
}

/** Both values name the same make in KNOWN_MAKES ("CAT" and "Caterpillar"): a spelling of the make, not a disagreement. */
function sameKnownMake(a: string | null, b: string | null): boolean {
  const ka = normalizePlate(a);
  const kb = normalizePlate(b);
  return isKnownMake(ka) && isKnownMake(kb) && KNOWN_MAKES[ka] === KNOWN_MAKES[kb];
}

/** Whole-value notes people type where a serial/model belongs (letters+digits only, after label and width folding). */
const NOTE_KEYS: ReadonlySet<string> = new Set([
  'SEETAG', 'ILLEGIBLE', 'UNREADABLE', 'NOTLEGIBLE', 'NOTREADABLE', 'CANTREAD', 'CANNOTREAD', 'ONFILE', 'NOTAG', 'NOPLATE', 'MISSING', 'PENDING',
  'NOSERIAL', 'NOTFOUND', 'NOTAVAILABLE', 'NOTVISIBLE', 'NONEVISIBLE', 'PAINTEDOVER', 'FADED', 'WORN', 'RUSTED',
]);
/** Words that make a SERIAL without any digit a note ("tag illegible", "plate missing", "not on file"). A real serial carries a digit. */
const NOTE_WORDS: ReadonlySet<string> = new Set([
  'SEE', 'TAG', 'PLATE', 'NAMEPLATE', 'FILE', 'ILLEGIBLE', 'LEGIBLE', 'UNREADABLE', 'READABLE', 'READ', 'MISSING', 'RUSTED', 'FADED', 'WORN',
  'PAINTED', 'VISIBLE', 'FOUND', 'AVAILABLE', 'SERIAL', 'UNIT', 'NONE', 'NA', 'CANT', 'CANNOT', 'NOT', 'NO', 'OFF', 'OVER',
]);
/** A SERIAL shorter than this is a stub ("0", "X", "12"). Only a stored record is judged by it: a person mid-typing is not junk. */
const MIN_SERIAL_LEN = 3;
/** A label printed before a value ("SN:", "Serial #", "S/N "). It counts only when a separator follows: "SN12345" is a serial, not a label plus 12345. */
const LABEL_PREFIX = /^\s*(?:s\s*\/\s*n|sn|serial(?:\s*(?:no|number))?)[:#.\-\s]+/i;

/** Present but unreadable by us, or too long: never comparable, even when it equals the plate. */
const isUnreadableRecord = (raw: string | null): boolean =>
  classifyRecordValue(raw) === 'unusable' || normalizePlate(raw).length > MAX_PLATE_VALUE_LEN;

/**
 * Is the stored/compared value a typed NOTE or a stub: something a plate cannot contradict (DR-PLATES-1, ruling DR-O89-4)?
 * Such a value is treated exactly like a blank placeholder: the plate FILLS it (a queue row with the one-click fix), it is never a
 * conflict. Notes are matched after NFKC and label removal, as whole words (any order, any extra words), so "SN: see tag",
 * "tag illegible" and fullwidth text are caught. A blank placeholder ("N/A", "?") is handled before this and stays blank.
 */
const isJunkRecord = (raw: string | null, component: VerdictComponent, field: VerdictField, source: 'record' | 'human'): boolean => {
  if (classifyRecordValue(raw) === 'blank' || isUnreadableRecord(raw)) return false;
  const text = String(raw ?? '').normalize('NFKC').replace(LABEL_PREFIX, '');
  if (NOTE_KEYS.has(text.toUpperCase().replace(/[^A-Z0-9]/g, '')) || /^\s*see[\s:,;-]+\S/i.test(text)) return true;
  if (field !== 'serial') return false;
  if (junkFlags(component, field, text).includes('note-not-value')) return true;
  const words = text.toUpperCase().split(/[^A-Z]+/).filter(Boolean);
  if (!/\d/.test(text) && words.some((w) => NOTE_WORDS.has(w))) return true;
  const norm = normalizePlate(text);
  return source === 'record' && (norm.length < MIN_SERIAL_LEN || /^0+$/.test(norm));
};

/** `comparedOverride` '' = judge as a blank (a typed note or stub); the displayed comparedValue stays what was stored. */
function decide(input: VerdictInput, reason: VerdictReason, rawPlate: string, facts: PhotoFacts, comparedOverride?: string): Verdict {
  const compared = comparedOverride ?? input.comparedValue;
  const decided = (kind: VerdictKind): Verdict => ({
    component: input.component, field: input.field, kind, confidence: 'high', reason,
    comparedValue: sanitizeDisplay(input.comparedValue), diffPositions: diffPositions(rawPlate, compared), ...facts,
  });
  const cmp = comparePlateValues(rawPlate, compared);
  // Two spellings of one known make agree, whoever typed them (equality, not softening: a person's "CAT" for a "CATERPILLAR" plate).
  if (input.field === 'make' && cmp !== 'blank' && sameKnownMake(rawPlate, compared)) return { ...decided('match'), diffPositions: [] };
  // Approving a plate value that is part of the stored record would CUT THE RECORD SHORT (plateTruncatesRecord, the rule the
  // proposal writer skips on too): never a one-click "use plate". Only a stored record is softened: a person's typed value is judged
  // as typed (part-way through typing is not truncation).
  if (input.comparedSource === 'record' && (cmp === 'near' || cmp === 'contained') && plateTruncatesRecord(rawPlate, compared)) {
    return { ...decided('unsure'), confidence: 'low', reason: 'contained' };
  }
  switch (cmp) {
    case 'blank': return decided('fill');
    case 'same': return { ...decided('match'), diffPositions: [] };
    case 'near': return decided(input.comparedSource === 'human' ? 'likely-typo' : 'conflict');
    case 'different': return decided('conflict');
    case 'format-variant': return { ...decided('format-variant'), confidence: 'low' };
    case 'lookalike': return { ...decided('unsure'), confidence: 'low', reason: 'lookalike' };
    // Containment is directional: the plate-is-part-of-the-record case returned unsure above. What is left (a record that is part
    // of the plate value, the plate filling it in; or a person's typed value) is a conflict.
    case 'contained': return decided('conflict');
  }
}

const photoFacts = (r: PlateReading, field: VerdictField, photosRead: number, photosAgreeing: number): PhotoFacts => ({
  photoId: r.photoId, storagePath: r.storagePath, plateValue: sanitizeDisplay(r.fields?.[field]?.value), photosRead, photosAgreeing,
});

/** All readings in `agreed` carry one value. Apply the anchor (make/model), then compare with the record. */
function judge(input: VerdictInput, cf: Confirmer, agreed: readonly PlateReading[], photosRead: number): Verdict {
  const { field } = input;
  let pool = agreed;
  if (field !== 'serial') {
    const states = agreed.map((r) => ({ r, state: anchorState(input, cf, r) }));
    pool = states.filter((s) => s.state === 'pass').map((s) => s.r);
    if (pool.length === 0) {
      const reason = states.some((s) => s.state === 'mismatch') ? 'anchor-mismatch' : 'anchor-unchecked';
      const shown = photoFacts(pickBest(agreed, field, cf), field, photosRead, agreed.length);
      return { ...empty(input, 'unsure', reason, photosRead), ...shown };
    }
  }
  const best = pickBest(pool, field, cf);
  const facts = photoFacts(best, field, photosRead, agreed.length);
  const plateRaw = best.fields[field]?.value ?? '';
  if (isUnreadableRecord(input.comparedValue)) return { ...empty(input, 'unsure', 'unusable-record', photosRead), ...facts };
  const junk = isJunkRecord(input.comparedValue, input.component, field, input.comparedSource)
    && comparePlateValues(plateRaw, input.comparedValue) !== 'same';
  const reason: VerdictReason = cf.of(best, field) === 'barcode' ? 'barcode-confirmed' : 'readers-agree';
  return decide(input, reason, plateRaw, facts, junk ? '' : undefined);
}

export function verdictFor(input: VerdictInput): Verdict {
  const { field } = input;
  if (input.readings.length === 0) return empty(input, 'no-plate', 'no-reading');
  const ambiguousAts = input.unitHasAts2 !== false;
  const own = input.readings.filter((r) => componentMatches(r, input.component, ambiguousAts));
  if (own.length === 0) return empty(input, 'unsure', 'wrong-component');
  const read = own.filter((r) => r.fields?.[field]?.status === 'read');
  if (read.length === 0) {
    const partial = own.some((r) => r.fields?.[field]?.status === 'partial');
    return empty(input, 'unsure', partial ? 'partial-read' : 'unreadable');
  }
  const cf = createConfirmer();
  const agreed = read.filter((r) => cf.of(r, field) !== null);
  if (agreed.length === 0) return empty(input, 'unsure', 'readers-disagree', read.length);
  const values = new Set(agreed.map((r) => normalizePlate(r.fields?.[field]?.value)));
  if (values.size > 1) return empty(input, 'unsure', 'photos-disagree', read.length);
  return judge(input, cf, agreed, read.length);
}
